// src/constants/messageTemplates.ts - Templates marketing (i18n namespace `marketing`)

import { CampaignChannel, CampaignType } from '../types/marketing';
import {
  createCampaignTemplateArrays,
  createCampaignTemplateDefaults,
  getDefaultWhatsappTemplate,
  getMarketingValidationMessage,
  getTemplateSuggestionLabels,
  getWhatsappTemplates,
} from '../utils/i18n/marketingTemplates';

// Variables disponibles dans les templates
export const TEMPLATE_VARIABLES = {
  clientName: '{clientName}',
  discountValue: '{discountValue}',
  discountAmount: '{discountAmount}',
  validUntil: '{validUntil}',
  loyaltyBalance: '{loyaltyBalance}',
  lastVisit: '{lastVisit}',
  businessName: '{businessName}',
  eventName: '{eventName}',
  age: '{age}',
} as const;

/** Templates WhatsApp — locale i18n courante. */
export const MESSAGE_TEMPLATES: Record<CampaignType, string[]> =
  createCampaignTemplateArrays(getWhatsappTemplates);

export const DEFAULT_TEMPLATES: Record<CampaignType, string> =
  createCampaignTemplateDefaults(getDefaultWhatsappTemplate);

export const TEMPLATE_SUGGESTION_LABELS: Record<CampaignType, string[]> =
  createCampaignTemplateArrays(getTemplateSuggestionLabels);

export const getRecommendedTemplateIndex = (
  campaignType: CampaignType,
  objective?: string
): number => {
  if (!objective) return 0;

  if (campaignType === 'retention' && objective === 'reactivation') return 0;
  if (campaignType === 'cycle_reminder' && objective === 'repeat_booking') return 0;
  if (campaignType === 'cross_sell' && objective === 'upsell') return 1;
  if (campaignType === 'cross_sell' && objective === 'product_conversion') return 0;
  if (campaignType === 'loyalty' && objective === 'loyalty_growth') return 0;

  return 0;
};

export const computeTemplateQualityScore = (
  template: string,
  channel: CampaignChannel
): { score: number; checks: string[] } => {
  let score = 100;
  const checks: string[] = [];

  if (!template.includes('{clientName}')) {
    score -= 25;
    checks.push(getMarketingValidationMessage('addClientName'));
  }

  if (!template.includes('{discountValue}')) {
    score -= 20;
    checks.push(getMarketingValidationMessage('addDiscountValue'));
  }

  if (!template.includes('{validUntil}')) {
    score -= 15;
    checks.push(getMarketingValidationMessage('addValidUntil'));
  }

  if (channel === 'sms' && template.length > 160) {
    score -= 20;
    checks.push(getMarketingValidationMessage('reduceSmsLength'));
  }

  if (template.length < 40) {
    score -= 10;
    checks.push(getMarketingValidationMessage('tooShort'));
  }

  return {
    score: Math.max(0, Math.min(100, score)),
    checks,
  };
};

const LOYALTY_LINE_RE = /cagnotte|loyalty wallet|wallet balance/i;

export const replaceTemplateVariables = (
  template: string,
  variables: Record<string, string | number>
): string => {
  let message = template;

  const loyaltyRaw = variables.loyaltyBalance;
  const loyaltyValue = Number(String(loyaltyRaw ?? '').replace(/[^0-9.-]/g, ''));
  const shouldHideLoyaltyLine = Number.isFinite(loyaltyValue) && loyaltyValue <= 0;

  if (shouldHideLoyaltyLine) {
    message = message
      .split('\n')
      .filter((line) => !line.includes('{loyaltyBalance}') && !LOYALTY_LINE_RE.test(line))
      .join('\n');
  }

  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    const valueAsString = String(value);

    if (key === 'discountValue' && /[%]|fcfa/i.test(valueAsString)) {
      message = message.replace(/\{discountValue\}\s*%/g, valueAsString);
    }

    message = message.replace(new RegExp(placeholder, 'g'), valueAsString);
  });

  return message
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const extractUsedVariables = (template: string): string[] => {
  const regex = /{([^}]+)}/g;
  const variables: string[] = [];
  let match;

  while ((match = regex.exec(template)) !== null) {
    variables.push(match[1]);
  }

  return variables.filter((value, index, self) => self.indexOf(value) === index);
};

export const validateTemplate = (
  template: string
): {
  isValid: boolean;
  errors: string[];
  usedVariables: string[];
} => {
  const errors: string[] = [];
  const usedVariables = extractUsedVariables(template);
  const availableVariables = Object.keys(TEMPLATE_VARIABLES);

  usedVariables.forEach((variable) => {
    if (!availableVariables.includes(variable)) {
      errors.push(
        getMarketingValidationMessage('unknownVariable', {
          variable: `{${variable}}`,
        })
      );
    }
  });

  if (template.length > 500) {
    errors.push(getMarketingValidationMessage('tooLong'));
  }

  if (!usedVariables.includes('clientName')) {
    errors.push(getMarketingValidationMessage('clientNameRequired'));
  }

  return {
    isValid: errors.length === 0,
    errors,
    usedVariables,
  };
};

export const validateTemplateForChannel = (
  template: string,
  channel: CampaignChannel
): {
  isValid: boolean;
  errors: string[];
  usedVariables: string[];
} => {
  const base = validateTemplate(template);
  const errors = [...base.errors];

  if (channel === 'sms') {
    const sample = replaceTemplateVariables(template, {
      clientName: 'Marie',
      discountValue: '15%',
      discountAmount: 5000,
      validUntil: '31/12',
      loyaltyBalance: 0,
      lastVisit: '01/01/2026',
      businessName: 'Nauvatek',
      eventName: 'Promo',
      age: 30,
    });
    const sampleWithUrl = `${sample} https://beauty.nauvatek.com/p/ABCDEFGH`;
    if (sampleWithUrl.length > 160) {
      errors.push(
        getMarketingValidationMessage('smsTooLong', { length: sampleWithUrl.length })
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    usedVariables: base.usedVariables,
  };
};
